/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Select,
  Stack,
  Textarea,
  TextInput,
  Button,
  Group,
  ActionIcon,
  Table,
  LoadingOverlay,
  Text,
  TagsInput,
  Tooltip
} from '@mantine/core';
import { IconTrash, IconPlus, IconSearch, IconCheck, IconCirclePlus } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import { masterQuestionService } from 'services/lia/masterQuestionService';
import { masterGroupService } from 'services/lia/masterGroupService';
import { UpdateMasterQuestionDto, RangeItem } from 'services/api';
import PageTitle from 'components/PageTitle';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import RangeTemplateSelector from 'components/RangeTemplateSelector';
import DataTables from 'components/DataTables';
import { useDebouncedValue } from '@mantine/hooks';

const items = [
  { title: 'Questions', href: '/app/lia/questions/master-questions' },
  { title: 'Edit Question', href: '' }
];

// Schema for range items
const rangeItemSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  value: z.string().min(1, 'Value is required'),
  sequence: z.number().optional(),
  msQuestionRangeItemID: z.string().optional()
});

const questionSchema = z.object({
  MsQuestionID: z.string(),
  Category: z.array(z.string()).default([]),
  Title: z.string().min(1, 'Question is required'),
  Description: z.string().optional(),
  Type: z.enum(['Choose One', 'Range', 'Free-text']),
  MsGroupQuestionID: z.string().min(1, 'Group Question is required'),
  Sequence: z.number().default(1),
  RangeItem: z.array(rangeItemSchema).optional().default([]),
  SkalaCategory: z.string().optional()
});

type FormData = z.infer<typeof questionSchema>;

const EditQuestionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const questionId = searchParams.get('id');
  const [opened, { open, close }] = useDisclosure();
  const [formData, setFormData] = useState<FormData | null>(null);

  // Group search and pagination states
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [groupPage, setGroupPage] = useState(1);
  const [debouncedGroupSearch] = useDebouncedValue(groupSearchQuery, 300);
  const [selectedGroupName, setSelectedGroupName] = useState('');

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(questionSchema),
    defaultValues: {
      MsQuestionID: questionId || '',
      RangeItem: [],
      Category: []
    }
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'RangeItem'
  });

  const questionType = watch('Type');

  // Group selection helper functions
  const groupColumns = [
    {
      heading: 'Group Name',
      props: 'title',
      sortable: true,
      active: true,
      disabled: false,
      id: 'title'
    },
    {
      heading: 'Description',
      props: 'desc',
      sortable: false,
      active: true,
      disabled: false,
      id: 'desc'
    },
    {
      heading: 'Actions',
      props: 'actions',
      sortable: false,
      active: true,
      disabled: false,
      id: 'actions'
    }
  ];

  const handleGroupSelect = (group: any) => {
    setValue('MsGroupQuestionID', group.msGroupQuestionID);
    setSelectedGroupName(group.title);
  };

  const prepareGroupTableData = (groups: any[], selectedValue: string) => {
    return groups.map((group) => ({
      ...group,
      actions: (
        <Tooltip
          label={selectedValue === group.msGroupQuestionID ? 'Selected' : 'Select this group'}
          position='top'
          withArrow
        >
          <ActionIcon
            size='lg'
            variant={selectedValue === group.msGroupQuestionID ? 'filled' : 'outline'}
            color={selectedValue === group.msGroupQuestionID ? 'green' : '#7B2183'}
            onClick={() => handleGroupSelect(group)}
            disabled={selectedValue === group.msGroupQuestionID}
          >
            {selectedValue === group.msGroupQuestionID ? (
              <IconCheck size={16} />
            ) : (
              <IconCirclePlus size={16} />
            )}
          </ActionIcon>
        </Tooltip>
      )
    }));
  };

  // Fetch group questions with search and pagination
  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['masterGroups', debouncedGroupSearch, groupPage],
    queryFn: () =>
      masterGroupService.getList({
        search: debouncedGroupSearch,
        page: groupPage,
        limit: 10
      }),
    staleTime: 5 * 60 * 1000
  });

  const { data: questionData, isLoading: isLoadingQuestion } = useQuery({
    queryKey: ['question', questionId],
    queryFn: async () => {
      if (!questionId) return null;
      return await masterQuestionService.getDetail(questionId);
    },
    enabled: !!questionId,
    retry: 1
  });

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => {
      const rangeItems: RangeItem[] =
        data.RangeItem?.map((item, index) => ({
          label: item.label,
          value: item.value,
          sequence: item.sequence || index + 1,
          msQuestionRangeItemID: item.msQuestionRangeItemID
        })) || [];

      const updateDto: UpdateMasterQuestionDto = {
        MsQuestionID: data.MsQuestionID,
        Category: data.Category.join(', '),
        Title: data.Title,
        Description: data.Description,
        SkalaCategory: data.SkalaCategory,
        Type: data.Type,
        MsGroupQuestionID: data.MsGroupQuestionID,
        Sequence: data.Sequence,
        RangeItem: data.Type === 'Range' ? rangeItems : undefined
      };

      return masterQuestionService.update(data.MsQuestionID, updateDto);
    },
    onSuccess: () => {
      navigate('/app/lia/questions/master-questions');
      showSuccessNotification({
        title: 'Updated Successfully!',
        message: 'Question has been updated successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to update question',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  useEffect(() => {
    if (questionData) {
      const question = questionData.data || questionData;

      reset({
        MsQuestionID: question.msQuestionID,
        Category: question.category
          ? question.category.split(',').map((cat: string) => cat.trim())
          : [],
        Title: question.title,
        Description: question.description,
        Type: question.type,
        MsGroupQuestionID: question.msGroupQuestionID,
        Sequence: question.sequence || 1,
        SkalaCategory: question.skalaCategory || '',
        RangeItem: []
      });

      if (question.type === 'Range' && question.rangeItem?.length) {
        replace(
          question.rangeItem.map((item: any) => ({
            label: item.label,
            value: item.value,
            sequence: item.sequence || 1,
            msQuestionRangeItemID: item.msQuestionRangeItemID
          }))
        );
      }
    }
  }, [questionData, reset, replace]);

  // Set selectedGroupName when groups data is loaded and form has a value
  useEffect(() => {
    const formGroupId = watch('MsGroupQuestionID');
    if (formGroupId && groupsData?.data) {
      const selectedGroup = groupsData.data.find(
        (group) => group.msGroupQuestionID === formGroupId
      );
      if (selectedGroup) {
        setSelectedGroupName(selectedGroup.title);
      }
    }
  }, [groupsData, watch]);

  const onSubmit = (data: FormData) => {
    setFormData(data);
    open();
  };

  const processData = async () => {
    if (formData) {
      updateMutation.mutate(formData);
    }
    close();
  };

  if (!questionId) {
    navigate('/app/lia/questions/master-questions');
    return null;
  }

  const isLoading = isLoadingQuestion || isLoadingGroups;

  const handleTemplateSelect = (templateItems: any[]) => {
    if (fields.length > 0) {
      remove();
    }

    if (templateItems && templateItems.length > 0) {
      templateItems.forEach((item: any) => {
        append({
          label: item.label,
          value: item.value,
          sequence: item.sequence || 1
        });
      });
    }
  };

  return (
    <>
      <PageTitle title='Edit Question' />
      <BreadcrumbsComponent items={items} />

      <ModalPopupConfirmationComponent
        buttonText='Update'
        statusProcess='edit'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processData}
      />

      <div style={{ marginTop: '20px', position: 'relative' }}>
        <LoadingOverlay visible={isLoading || updateMutation.isPending} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap='md'>
            <Controller
              name='Title'
              control={control}
              render={({ field }) => (
                <Textarea
                  autosize
                  minRows={2}
                  maxRows={10}
                  label='Question'
                  placeholder='Input question...'
                  error={errors.Title?.message}
                  required
                  disabled={isLoading}
                  {...field}
                />
              )}
            />

            <Controller
              name='Category'
              control={control}
              render={({ field }) => (
                <TagsInput
                  label='Question Categories'
                  placeholder='Type and press Enter to add categories'
                  description='Add multiple categories by typing and pressing Enter'
                  data={[]}
                  error={errors.Category?.message}
                  value={field.value}
                  onChange={field.onChange}
                  clearable
                  maxTags={10}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name='SkalaCategory'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Skala Category'
                  placeholder='Input skala category...'
                  error={errors.Title?.message}
                  required
                  {...field}
                />
              )}
            />

            <Controller
              name='Description'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Question Description'
                  placeholder='Input description...'
                  description='Optional description for the question'
                  disabled={isLoading}
                  {...field}
                />
              )}
            />

            <Controller
              name='Type'
              control={control}
              render={({ field }) => (
                <Select
                  label='Type of Question'
                  placeholder='Select type'
                  required
                  data={[
                    { value: 'Choose One', label: 'Choose One' },
                    { value: 'Range', label: 'Range' },
                    { value: 'Free-text', label: 'Free-text' }
                  ]}
                  error={errors.Type?.message}
                  disabled={isLoading}
                  {...field}
                />
              )}
            />
            {questionType === 'Range' && (
              <Stack gap='md'>
                <Group justify='space-between'>
                  <Text size='sm' fw={500}>
                    Range Items
                  </Text>
                  <RangeTemplateSelector onSelect={handleTemplateSelect} />
                </Group>
                <Table
                  verticalSpacing='sm'
                  horizontalSpacing='sm'
                  withTableBorder
                  withColumnBorders
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Label</Table.Th>
                      <Table.Th>Value</Table.Th>
                      <Table.Th w={40} /> {/* Column for delete button */}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {fields.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={3} style={{ textAlign: 'center' }}>
                          No range items added yet
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      fields.map((field, index) => (
                        <Table.Tr key={field.id}>
                          <Table.Td>
                            <Controller
                              name={`RangeItem.${index}.label`}
                              control={control}
                              render={({ field }) => (
                                <TextInput
                                  placeholder='e.g., Poor, Fair, Good'
                                  error={errors.RangeItem?.[index]?.label?.message}
                                  disabled={isLoading}
                                  {...field}
                                />
                              )}
                            />
                            <Controller
                              name={`RangeItem.${index}.msQuestionRangeItemID`}
                              control={control}
                              render={({ field }) => <input type='hidden' {...field} />}
                            />
                          </Table.Td>
                          <Table.Td>
                            <Controller
                              name={`RangeItem.${index}.value`}
                              control={control}
                              render={({ field }) => (
                                <TextInput
                                  placeholder='e.g., 1, 2, 3'
                                  error={errors.RangeItem?.[index]?.value?.message}
                                  disabled={isLoading}
                                  {...field}
                                />
                              )}
                            />
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon
                              color='red'
                              onClick={() => remove(index)}
                              variant='light'
                              disabled={isLoading}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>

                <Button
                  variant='light'
                  color='#7B2183'
                  leftSection={<IconPlus size={16} />}
                  onClick={() => append({ label: '', value: '', sequence: fields.length + 1 })}
                  mt='xs'
                  disabled={isLoading}
                >
                  Add Range Item
                </Button>
              </Stack>
            )}

            <Controller
              name='MsGroupQuestionID'
              control={control}
              render={({ field: _field }) => {
                const selectedValue = watch('MsGroupQuestionID') || '';
                return (
                  <Stack gap='sm'>
                    <div>
                      <label
                        style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          marginBottom: '8px',
                          display: 'block'
                        }}
                      >
                        Group Question <span style={{ color: 'red' }}>*</span>
                      </label>

                      <TextInput
                        placeholder='Search group master question...'
                        value={groupSearchQuery}
                        onChange={(event) => setGroupSearchQuery(event.currentTarget.value)}
                        leftSection={<IconSearch size={16} />}
                        mb='sm'
                      />

                      <DataTables
                        data={prepareGroupTableData(groupsData?.data || [], selectedValue)}
                        columns={groupColumns}
                        totalData={groupsData?.totalPages ? groupsData.totalPages * 10 : 0}
                        currentPage={groupPage}
                        handleChangePage={setGroupPage}
                        loading={isLoadingGroups}
                        emptyMessage='No groups found'
                        withPagination
                      />

                      {errors.MsGroupQuestionID && (
                        <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                          {errors.MsGroupQuestionID.message}
                        </div>
                      )}
                    </div>
                  </Stack>
                );
              }}
            />

            {/* <Controller
              name='Sequence'
              control={control}
              render={({ field: { onChange, value, ...rest } }) => (
                <TextInput
                  label='Sequence'
                  placeholder='1'
                  description='Order of this question in the group'
                  type='number'
                  value={value?.toString() || '1'}
                  onChange={(e) => onChange(parseInt(e.target.value) || 1)}
                  disabled={isLoading}
                  {...rest}
                />
              )}
            /> */}

            {/* Hidden field for MsQuestionID */}
            <Controller
              name='MsQuestionID'
              control={control}
              render={({ field }) => <input type='hidden' {...field} />}
            />
          </Stack>

          <Group justify='flex-end' mt='xl'>
            <SecondaryButton
              text='Cancel'
              onClick={() => navigate('/app/lia/questions/master-questions')}
              mr={10}
            />
            <PrimaryButton
              text='Update'
              type='submit'
              loading={isSubmitting || updateMutation.isPending}
            />
          </Group>
        </form>
      </div>
    </>
  );
};

export default EditQuestionPage;
